/**
 * Centralised API helper.
 *
 * - REACT_APP_API_URL: production backend origin (e.g. https://api.numidatech.com)
 * - Falls back to http://localhost:3001 for local development.
 * - REACT_APP_COGNITO_CLIENT_ID: when set, uses Cognito id_tokens instead of
 *   self-signed JWTs (the /auth/sync endpoint syncs the user to the local DB).
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPut(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPatch(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PATCH ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
}

export async function apiUpload(path, formData) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error(`UPLOAD ${path} failed: ${res.status}`);
  return res.json();
}

/** Base URL without path — exposed for redirect-building (e.g. OAuth flows). */
export const API_BASE_URL = API_BASE;

// ── Cognito helpers (only used when REACT_APP_COGNITO_CLIENT_ID is set) ──────

export function isCognitoMode() {
  return !!process.env.REACT_APP_COGNITO_CLIENT_ID;
}

export function cognitoAuthorizeUrl() {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_REDIRECT_URI;
  return (
    `https://${domain}/oauth2/authorize` +
    `?identity_provider=Google` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code` +
    `&client_id=${clientId}` +
    `&scope=openid+email+profile`
  );
}

export async function cognitoExchangeCode(code) {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_REDIRECT_URI;

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      redirect_uri: redirectUri,
      code,
    }),
  });

  if (!res.ok) throw new Error('Token exchange failed');
  return res.json(); // { id_token, access_token, refresh_token, ... }
}

// ── Silent token refresh ──────────────────────────────────────────────────────
//
// Access tokens are short-lived on purpose. Rather than bouncing an idle user
// back to the login screen, we keep a longer-lived refresh token around and
// use it to mint a fresh access token transparently:
//   - Cognito mode: exchanges the Cognito refresh_token via its /oauth2/token
//     endpoint (grant_type=refresh_token).
//   - Local mode: exchanges our own refresh JWT via POST /auth/refresh.
//
// `installFetchAuthRefresh()` patches window.fetch once so every existing
// call site (both the helpers above and the raw `fetch(...)` calls used
// elsewhere in the app) automatically benefits, with no call-site changes.

function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isTokenExpiringSoon(bufferMs = 60000) {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return Date.now() >= payload.exp * 1000 - bufferMs;
}

async function refreshCognitoToken() {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken,
    }),
  });

  if (!res.ok) throw new Error('Cognito token refresh failed');
  const data = await res.json();
  if (!data.id_token) throw new Error('No id_token in refresh response');

  localStorage.setItem('token', data.id_token);
  // Cognito only returns a new refresh_token when rotation is enabled; keep
  // the existing one otherwise.
  if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
  return data.id_token;
}

async function refreshLocalToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');

  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) throw new Error('Token refresh failed');
  const data = await res.json();
  if (!data.token) throw new Error('No token in refresh response');

  localStorage.setItem('token', data.token);
  return data.token;
}

let refreshPromise = null;

/** Refreshes the access token, de-duping concurrent callers onto one request. */
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (isCognitoMode() ? refreshCognitoToken() : refreshLocalToken()).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Refreshes the access token if it's expired or close to expiring. Never throws. */
async function ensureFreshToken() {
  if (!isTokenExpiringSoon()) return;
  try {
    await refreshAccessToken();
  } catch (error) {
    // No usable refresh token, or the refresh token itself expired — the
    // stale access token will fail its own request normally, and the app's
    // existing "verification failed -> back to login" handling takes over.
    console.warn('Silent token refresh failed:', error.message);
  }
}

function withCurrentAuthHeader(init) {
  const headers = new Headers(init?.headers || {});
  if (headers.has('Authorization')) {
    const token = localStorage.getItem('token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

let fetchAuthRefreshInstalled = false;

function installFetchAuthRefresh() {
  if (fetchAuthRefreshInstalled) return;
  fetchAuthRefreshInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isApiCall = url.startsWith(API_BASE);
    // These endpoints are called without (or before establishing) a valid
    // access token, so they must bypass the refresh dance entirely.
    const isUnauthenticatedAuthEndpoint = /\/auth\/(refresh|google|sync)(\?|$)/.test(url);

    if (!isApiCall || isUnauthenticatedAuthEndpoint) {
      return originalFetch(input, init);
    }

    await ensureFreshToken();
    let response = await originalFetch(input, withCurrentAuthHeader(init));

    if (response.status === 401 && localStorage.getItem('refreshToken')) {
      try {
        await refreshAccessToken();
        response = await originalFetch(input, withCurrentAuthHeader(init));
      } catch (error) {
        // Refresh token is gone/invalid — surface the original 401 so the
        // app's existing "session expired" handling redirects to login.
      }
    }

    return response;
  };
}

installFetchAuthRefresh();
